/**
 * O convênio do paciente e as operadoras (#811, clínica).
 *
 * Só nas organizações com a funcionalidade "clinica" ligada; sem ela, as rotas
 * respondem 404. A carteirinha entra inteira e sai só com os quatro últimos
 * caracteres: o número inteiro nunca volta da API.
 */

import { z } from 'zod';

const DataSchema = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/);

export const ConvenioDoPacienteSchema = z.object({
  id: z.string().uuid(),
  modalidade: z.enum(['particular', 'convenio']),
  operadora: z.object({ id: z.string().uuid(), nome: z.string() }).nullable(),
  plano: z.string().nullable(),
  /** Os quatro últimos caracteres da carteirinha. */
  carteirinha_final: z.string().nullable(),
  /** `AAAA-MM-DD`. */
  validade: DataSchema.nullable(),
  /** Se o paciente é o titular do plano, e não dependente. */
  titular: z.boolean(),
  principal: z.boolean(),
  versao: z.number().int().min(1),
});
export type ConvenioDoPaciente = z.infer<typeof ConvenioDoPacienteSchema>;

export const ConveniosDoPacienteSchema = z.object({
  /** O principal primeiro. */
  itens: z.array(ConvenioDoPacienteSchema),
});
export type ConveniosDoPaciente = z.infer<typeof ConveniosDoPacienteSchema>;

const CamposDoConvenio = {
  modalidade: z.enum(['particular', 'convenio']),
  operadora_id: z.string().uuid().nullable(),
  plano: z.string().max(120).nullable(),
  validade: DataSchema.nullable(),
  titular: z.boolean(),
  principal: z.boolean(),
};

export const CriarConvenioDoPacienteSchema = z
  .object({
    ...CamposDoConvenio,
    /** O número inteiro, que é cifrado ao chegar. */
    carteirinha: z.string().max(40).nullable(),
  })
  .strict();
export type CriarConvenioDoPaciente = z.infer<typeof CriarConvenioDoPacienteSchema>;

export const EditarConvenioDoPacienteSchema = z
  .object({
    ...CamposDoConvenio,
    /** Ausente mantém a carteirinha; `null` tira; texto troca. */
    carteirinha: z.string().max(40).nullable().optional(),
    versao: z.number().int().min(1),
  })
  .strict();
export type EditarConvenioDoPaciente = z.infer<typeof EditarConvenioDoPacienteSchema>;

export const OperadoraSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  /** Os seis dígitos do registro na ANS. */
  registro_ans: z.string().nullable(),
  /** `manual`: cadastrada em Configurações. `agenda`: mantida pela agenda da clínica, não se edita. */
  origem: z.enum(['manual', 'agenda']),
  ativa: z.boolean(),
});
export type Operadora = z.infer<typeof OperadoraSchema>;

export const OperadorasSchema = z.object({ itens: z.array(OperadoraSchema) });
export type Operadoras = z.infer<typeof OperadorasSchema>;

export const CriarOperadoraSchema = z
  .object({
    nome: z.string().max(120),
    registro_ans: z.string().max(20).nullable(),
  })
  .strict();
export type CriarOperadora = z.infer<typeof CriarOperadoraSchema>;

export const EditarOperadoraSchema = z
  .object({
    nome: z.string().max(120),
    registro_ans: z.string().max(20).nullable(),
    ativa: z.boolean(),
  })
  .strict();
export type EditarOperadora = z.infer<typeof EditarOperadoraSchema>;
